import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import GlobalModal from '../../components/GlobalModal';
import ActivateModal from './ActivateModal';
import CompaniesListItem from './CompaniesListItem';

// TYPES
import { OrganisationListOrganisation, DomainOrganisationStatus } from '../../api/models';

type CompaniesListVariant = 'active' | 'deactivated' | 'incomplete';

interface CompaniesListTypes {
	variant: CompaniesListVariant;
	items: OrganisationListOrganisation[];
	onActivate?: (item: OrganisationListOrganisation) => void;
}

const CompaniesList: React.FC<CompaniesListTypes> = ({ variant, items, onActivate }) => {
	if (!items.length) {
		return null;
	}

	const gridClassName = variant === 'deactivated' ? 'companies-grid deactivated' : 'companies-grid standard';

	return (
		<section className="info-section companies-list-section">
			<div className={gridClassName}>
				{items.map((item) => (
					<CompaniesListItem key={item.id} variant={variant} item={item} onActivate={onActivate} />
				))}
			</div>
		</section>
	);
};

const Companies = () => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to control Modal
	const [showActivateModal, setShowActivateModal] = React.useState<boolean>(false);

	// Set up data state
	const [companiesData, setCompaniesData] = React.useState<OrganisationListOrganisation[]>([]);

	const getCompaniesData = async () => {
		setIsLoading(true);

		const response = await apibridge.getCompaniesData();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result) {
			setIsLoading(false);

			setCompaniesData(response.result);
		} else {
			setIsLoading(false);
		}
	};

	React.useEffect(() => {
		getCompaniesData();
	}, []);

	// Filter data
	const activeCompanies = companiesData.filter(
		(item) => item.status === DomainOrganisationStatus.Active && !item.isLocked
	);
	const deactiveCompanies = companiesData.filter(
		(item) => item.status === DomainOrganisationStatus.Inactive && !item.isLocked
	);
	const incompleteCompanies = companiesData.filter(
		(item) => item.status === DomainOrganisationStatus.Pending && !item.isLocked
	);

	// State to control active tab and active content
	const [activeTab, setActiveTab] = React.useState<string>('active');

	// Activate organisation
	const [organisationDetail, setOrganisationDetail] = React.useState<OrganisationListOrganisation>({});

	const activateOrganisation = async (item: OrganisationListOrganisation) => {
		setOrganisationDetail(item);

		setShowActivateModal(true);
	};

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="page-header-section mtop-40">
									<div className="header-left">
										<Button
											onClick={() => setActiveTab('active')}
											className={`staff-category pdl-0 ${activeTab === 'active' ? 'active' : ''}`}
										>
											Active
										</Button>

										<Button
											onClick={() => setActiveTab('deactive')}
											className={`staff-category ${activeTab === 'deactive' ? 'active' : ''}`}
										>
											Deactivated
										</Button>

										<Button
											onClick={() => setActiveTab('incomplete')}
											className={`staff-category ${activeTab === 'incomplete' ? 'active' : ''}`}
										>
											Incomplete
										</Button>
									</div>
								</div>

								{activeTab === 'active' && <CompaniesList variant="active" items={activeCompanies} />}

								{activeTab === 'deactive' && (
									<CompaniesList variant="deactivated" items={deactiveCompanies} onActivate={activateOrganisation} />
								)}

								{activeTab === 'incomplete' && <CompaniesList variant="incomplete" items={incompleteCompanies} />}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}

			<GlobalModal isOpen={showActivateModal} setIsOpen={setShowActivateModal} titleText="Activate organisation">
				<ActivateModal
					organisationDetail={organisationDetail}
					getCompaniesData={getCompaniesData}
					setActiveTab={setActiveTab}
				/>
			</GlobalModal>
		</div>
	);
};

export default Companies;
